import React, { useEffect, useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    TextField,
    CircularProgress,
    Autocomplete,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Snackbar,
    Alert,
    Chip,
    Divider,
    Tab,
    Tabs,
    Paper,
    Avatar,
    Stack,
    useTheme,
    useMediaQuery,
    Tooltip,
    Fade,
    Badge,
    alpha,
    ButtonGroup,
    Drawer,
    Checkbox,
    Switch,
    FormControlLabel,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip as ReTooltip,
    Legend,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    ResponsiveContainer,
    CartesianGrid,
    AreaChart,
    Area,
} from 'recharts';
import { collection, query, where, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase-config';
import {
    Delete,
    TrendingUp,
    TrendingDown,
    Restaurant,
    Warning,
    CalendarToday,
    AttachMoney,
    Assessment,
    FilterList,
    Close,
    Refresh,
    Visibility,
    RestaurantMenu,
    DateRange,
    Timeline,
    Dashboard,
    CompareArrows,
    List as ListIcon,
    FilterAlt,
    Speed,
    Insights,
    BarChart as BarChartIcon,
    ShowChart,
    Image as ImageIcon,
    ZoomIn,
    DeleteSweep,
} from '@mui/icons-material';
import { useAuth } from '../auth-context';

const formatDate = (date) => date.toISOString().split('T')[0];

// Enhanced color palette
const CHART_COLORS = {
    primary: '#D32F2F',
    secondary: '#F4B400',
    success: '#16A34A',
    info: '#1976D2',
    warning: '#F59E0B',
    error: '#DC2626',

    gradients: {
        primary: 'linear-gradient(135deg, #D32F2F 0%, #FF6659 100%)',
        secondary: 'linear-gradient(135deg, #F4B400 0%, #FFE54C 100%)',
        success: 'linear-gradient(135deg, #16A34A 0%, #4AD66D 100%)',
        info: 'linear-gradient(135deg, #1976D2 0%, #63A4FF 100%)',
        warning: 'linear-gradient(135deg, #F59E0B 0%, #FFCF4C 100%)',
        error: 'linear-gradient(135deg, #DC2626 0%, #FF6659 100%)',
    },

    pastel: [
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
        '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B739', '#52B788'
    ],
};

const TIME_PERIODS = [
    { value: 'today', label: 'Today', icon: <CalendarToday /> },
    { value: 'week', label: 'Last 7 Days', icon: <DateRange /> },
    { value: 'month', label: 'Last 30 Days', icon: <CalendarToday /> },
    { value: 'quarter', label: 'Last 90 Days', icon: <DateRange /> },
    { value: 'year', label: 'Last Year', icon: <CalendarToday /> },
    { value: 'custom', label: 'Custom Range', icon: <FilterAlt /> },
];

const CHART_TYPES = {
    AREA: 'area',
    LINE: 'line',
    BAR: 'bar',
};

const EnhancedWasteManagementTab = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const { userRole } = useAuth();

    const [loading, setLoading] = useState(true);
    const [wasteItems, setWasteItems] = useState([]);
    const [restaurants, setRestaurants] = useState([]);
    const [itemsList, setItemsList] = useState([]);

    // Filters
    const [filterRestaurant, setFilterRestaurant] = useState('');
    const [filterItem, setFilterItem] = useState(null);
    const [timePeriod, setTimePeriod] = useState('month');
    const [filterStartDate, setFilterStartDate] = useState(() => {
        let d = new Date();
        d.setDate(d.getDate() - 30);
        return formatDate(d);
    });
    const [filterEndDate, setFilterEndDate] = useState(() => formatDate(new Date()));

    // UI States
    const [currentTab, setCurrentTab] = useState(0);
    const [chartType, setChartType] = useState(CHART_TYPES.AREA);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

    // Delete functionality
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Comparison
    const [compareEnabled, setCompareEnabled] = useState(false);
    const [previousPeriodItems, setPreviousPeriodItems] = useState([]);
    const [previousPeriodLoading, setPreviousPeriodLoading] = useState(false);

    // Image preview
    const [imageDialogOpen, setImageDialogOpen] = useState(false);
    const [imageDialogUrl, setImageDialogUrl] = useState('');

    // Multi-select deletion
    const [selectedRows, setSelectedRows] = useState([]);
    const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
    const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

    // Update date range based on time period
    useEffect(() => {
        const today = new Date();
        let startDate = new Date();

        switch (timePeriod) {
            case 'today':
                startDate = new Date();
                break;
            case 'week':
                startDate.setDate(today.getDate() - 7);
                break;
            case 'month':
                startDate.setDate(today.getDate() - 30);
                break;
            case 'quarter':
                startDate.setDate(today.getDate() - 90);
                break;
            case 'year':
                startDate.setDate(today.getDate() - 365);
                break;
            default:
                return;
        }

        setFilterStartDate(formatDate(startDate));
        setFilterEndDate(formatDate(today));
    }, [timePeriod]);

    // Fetch waste data
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const qBase = collection(db, 'wastage');
                const q = query(
                    qBase,
                    where('date', '>=', filterStartDate),
                    where('date', '<=', filterEndDate),
                    orderBy('date', 'desc')
                );

                const snapshot = await getDocs(q);
                const items = snapshot.docs.map((doc) => ({
                    id: doc.id,  // Ensure ID is set
                    ...doc.data()
                })).filter(item => item.id);
                setWasteItems(items);

                // Extract unique items
                const uniqueItemsSet = new Set();
                items.forEach(i => {
                    if (i.itemName && typeof i.itemName === "string") {
                        uniqueItemsSet.add(i.itemName.trim());
                    }
                });
                setItemsList(Array.from(uniqueItemsSet).sort());
            } catch (error) {
                console.error('Error fetching waste data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [filterStartDate, filterEndDate]);

    // Fetch previous period data for comparison
    useEffect(() => {
        if (!compareEnabled) {
            setPreviousPeriodItems([]);
            return;
        }

        const fetchPreviousPeriod = async () => {
            setPreviousPeriodLoading(true);
            try {
                const currentStart = new Date(filterStartDate);
                const currentEnd = new Date(filterEndDate);
                const daysDiff = Math.ceil((currentEnd - currentStart) / (1000 * 60 * 60 * 24));

                const prevEnd = new Date(currentStart);
                prevEnd.setDate(prevEnd.getDate() - 1);
                const prevStart = new Date(prevEnd);
                prevStart.setDate(prevStart.getDate() - daysDiff);

                const qBase = collection(db, 'wastage');
                const q = query(
                    qBase,
                    where('date', '>=', formatDate(prevStart)),
                    where('date', '<=', formatDate(prevEnd)),
                    orderBy('date', 'desc')
                );

                const snapshot = await getDocs(q);
                const items = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data()
                })).filter(item => item.id);
                setPreviousPeriodItems(items);
            } catch (error) {
                console.error('Error fetching previous period data:', error);
            } finally {
                setPreviousPeriodLoading(false);
            }
        };

        fetchPreviousPeriod();
    }, [compareEnabled, filterStartDate, filterEndDate]);

    // Fetch restaurants
    useEffect(() => {
        const fetchRestaurants = async () => {
            try {
                const qUsers = collection(db, 'users');
                const snapshot = await getDocs(qUsers);
                const rests = snapshot.docs.map(doc => ({
                    id: doc.id,
                    name: doc.data().restaurantName || doc.data().name || doc.id,
                }));
                setRestaurants(rests);
            } catch (e) {
                console.error('Error fetching restaurants:', e);
            }
        };
        fetchRestaurants();
    }, []);

    // Filter waste items
    const filteredWasteItems = useMemo(() => {
        let filtered = [...wasteItems];
        if (filterRestaurant) {
            filtered = filtered.filter(item => item.userId === filterRestaurant);
        }
        if (filterItem) {
            filtered = filtered.filter(item => item.itemName && item.itemName.trim() === filterItem);
        }
        return filtered.filter(item => item && item.id);;
    }, [wasteItems, filterRestaurant, filterItem]);

    // DYNAMIC CALCULATIONS
    const totalWasteValue = useMemo(
        () => filteredWasteItems.reduce((sum, item) => sum + (parseFloat(item.estimatedValue) || 0), 0),
        [filteredWasteItems]
    );

    const totalWasteEvents = useMemo(() => filteredWasteItems.length, [filteredWasteItems]);

    // Period stats - DYNAMIC
    const periodStats = useMemo(() => {
        const now = new Date();
        const today = formatDate(now);

        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        const weekStart = formatDate(weekAgo);

        const monthAgo = new Date();
        monthAgo.setDate(monthAgo.getDate() - 30);
        const monthStart = formatDate(monthAgo);

        const dailyItems = filteredWasteItems.filter(item => item.date === today);
        const weeklyItems = filteredWasteItems.filter(item => item.date >= weekStart);
        const monthlyItems = filteredWasteItems.filter(item => item.date >= monthStart);

        return {
            daily: {
                quantity: dailyItems.length,
                value: dailyItems.reduce((sum, item) => sum + (parseFloat(item.estimatedValue) || 0), 0),
            },
            weekly: {
                quantity: weeklyItems.length,
                value: weeklyItems.reduce((sum, item) => sum + (parseFloat(item.estimatedValue) || 0), 0),
            },
            monthly: {
                quantity: monthlyItems.length,
                value: monthlyItems.reduce((sum, item) => sum + (parseFloat(item.estimatedValue) || 0), 0),
            },
        };
    }, [filteredWasteItems]);

    // Top wasted item - DYNAMIC
    const topWastedItem = useMemo(() => {
        const itemTotals = {};
        filteredWasteItems.forEach(item => {
            const name = item.itemName || 'Unknown';
            const value = parseFloat(item.estimatedValue) || 0;
            itemTotals[name] = (itemTotals[name] || 0) + value;
        });

        const sorted = Object.entries(itemTotals).sort((a, b) => b[1] - a[1]);
        return sorted.length > 0 ? { name: sorted[0][0], value: sorted[0][1] } : { name: 'N/A', value: 0 };
    }, [filteredWasteItems]);

    // Top wasting restaurant - DYNAMIC
    const topWastingRestaurant = useMemo(() => {
        const restaurantTotals = {};
        filteredWasteItems.forEach(item => {
            const userId = item.userId || 'Unknown';
            const value = parseFloat(item.estimatedValue) || 0;
            restaurantTotals[userId] = (restaurantTotals[userId] || 0) + value;
        });

        const sorted = Object.entries(restaurantTotals).sort((a, b) => b[1] - a[1]);
        if (sorted.length === 0) return { name: 'N/A', value: 0 };

        const topRestId = sorted[0][0];
        const topRestValue = sorted[0][1];
        const restaurant = restaurants.find(r => r.id === topRestId);
        return { name: restaurant?.name || 'Unknown', value: topRestValue };
    }, [filteredWasteItems, restaurants]);

    // Main reason for waste - DYNAMIC
    const mainWasteReason = useMemo(() => {
        const reasonCounts = {};
        filteredWasteItems.forEach(item => {
            const reason = item.reason || 'Unknown';
            reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
        });

        const sorted = Object.entries(reasonCounts).sort((a, b) => b[1] - a[1]);
        return sorted.length > 0 ? sorted[0][0] : 'N/A';
    }, [filteredWasteItems]);

    const trendData = useMemo(() => {
        const dateMap = {};
        filteredWasteItems.forEach(item => {
            const date = item.date || formatDate(new Date());
            const value = parseFloat(item.estimatedValue) || 0;
            if (!dateMap[date]) {
                dateMap[date] = { date, value: 0, count: 0 };
            }
            dateMap[date].value += value;
            dateMap[date].count += 1;
        });

        return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
    }, [filteredWasteItems]);

    // Pie chart data by reason - DYNAMIC
    const reasonPieData = useMemo(() => {
        const reasonTotals = {};
        filteredWasteItems.forEach(item => {
            const reason = item.reason || 'Unknown';
            const value = parseFloat(item.estimatedValue) || 0;
            reasonTotals[reason] = (reasonTotals[reason] || 0) + value;
        });

        return Object.entries(reasonTotals).map(([name, value]) => ({ name, value }));
    }, [filteredWasteItems]);

    // Restaurant comparison data - DYNAMIC
    const restaurantComparisonData = useMemo(() => {
        const restTotals = {};
        filteredWasteItems.forEach(item => {
            const userId = item.userId || 'Unknown';
            const value = parseFloat(item.estimatedValue) || 0;
            if (!restTotals[userId]) {
                restTotals[userId] = { value: 0, count: 0 };
            }
            restTotals[userId].value += value;
            restTotals[userId].count += 1;
        });

        return Object.entries(restTotals).map(([userId, data]) => {
            const restaurant = restaurants.find(r => r.id === userId);
            return {
                name: restaurant?.name || 'Unknown',
                value: data.value,
                count: data.count,
            };
        }).sort((a, b) => b.value - a.value);
    }, [filteredWasteItems, restaurants]);

    // Daily average - DYNAMIC
    const dailyAverage = useMemo(() => {
        const daysDiff = Math.ceil((new Date(filterEndDate) - new Date(filterStartDate)) / (1000 * 60 * 60 * 24)) || 1;
        return totalWasteValue / daysDiff;
    }, [totalWasteValue, filterStartDate, filterEndDate]);

    // Previous period comparison metrics
    const comparisonMetrics = useMemo(() => {
        if (!compareEnabled || previousPeriodItems.length === 0) return null;

        let prevFiltered = [...previousPeriodItems];
        if (filterRestaurant) {
            prevFiltered = prevFiltered.filter(item => item.userId === filterRestaurant);
        }
        if (filterItem) {
            prevFiltered = prevFiltered.filter(item => item.itemName && item.itemName.trim() === filterItem);
        }

        const prevTotalValue = prevFiltered.reduce((sum, item) => sum + (parseFloat(item.estimatedValue) || 0), 0);
        const prevTotalEvents = prevFiltered.length;

        const currentStart = new Date(filterStartDate);
        const currentEnd = new Date(filterEndDate);
        const daysDiff = Math.ceil((currentEnd - currentStart) / (1000 * 60 * 60 * 24)) || 1;
        const prevDailyAvg = prevTotalValue / daysDiff;

        const valueChange = prevTotalValue > 0 ? ((totalWasteValue - prevTotalValue) / prevTotalValue * 100) : 0;
        const eventsChange = prevTotalEvents > 0 ? ((totalWasteEvents - prevTotalEvents) / prevTotalEvents * 100) : 0;
        const avgChange = prevDailyAvg > 0 ? ((dailyAverage - prevDailyAvg) / prevDailyAvg * 100) : 0;

        // Build previous period trend data
        const prevDateMap = {};
        prevFiltered.forEach(item => {
            const date = item.date || formatDate(new Date());
            const value = parseFloat(item.estimatedValue) || 0;
            if (!prevDateMap[date]) {
                prevDateMap[date] = { date, value: 0, count: 0 };
            }
            prevDateMap[date].value += value;
            prevDateMap[date].count += 1;
        });
        const prevTrendData = Object.values(prevDateMap).sort((a, b) => a.date.localeCompare(b.date));

        return {
            prevTotalValue,
            prevTotalEvents,
            prevDailyAvg,
            valueChange,
            eventsChange,
            avgChange,
            prevTrendData,
            // Also compute previous period date range for display
            prevStartDate: (() => {
                const currentStart = new Date(filterStartDate);
                const currentEnd = new Date(filterEndDate);
                const daysDiff = Math.ceil((currentEnd - currentStart) / (1000 * 60 * 60 * 24));
                const prevEnd = new Date(currentStart);
                prevEnd.setDate(prevEnd.getDate() - 1);
                const prevStart = new Date(prevEnd);
                prevStart.setDate(prevStart.getDate() - daysDiff);
                return prevStart.toISOString().split('T')[0];
            })(),
            prevEndDate: (() => {
                const currentStart = new Date(filterStartDate);
                const prevEnd = new Date(currentStart);
                prevEnd.setDate(prevEnd.getDate() - 1);
                return prevEnd.toISOString().split('T')[0];
            })(),
            // Quantity comparison
            prevTotalQuantity: prevFiltered.reduce((sum, item) => sum + (parseFloat(item.wastedQuantity) || 0), 0),
            currentTotalQuantity: filteredWasteItems.reduce((sum, item) => sum + (parseFloat(item.wastedQuantity) || 0), 0),
            // Top waste item comparison
            prevTopItem: (() => {
                const itemMap = {};
                prevFiltered.forEach(item => {
                    const name = item.itemName || 'Unknown';
                    itemMap[name] = (itemMap[name] || 0) + (parseFloat(item.estimatedValue) || 0);
                });
                const sorted = Object.entries(itemMap).sort((a, b) => b[1] - a[1]);
                return sorted.length > 0 ? { name: sorted[0][0], value: sorted[0][1] } : { name: 'N/A', value: 0 };
            })(),
            currentTopItem: (() => {
                const itemMap = {};
                filteredWasteItems.forEach(item => {
                    const name = item.itemName || 'Unknown';
                    itemMap[name] = (itemMap[name] || 0) + (parseFloat(item.estimatedValue) || 0);
                });
                const sorted = Object.entries(itemMap).sort((a, b) => b[1] - a[1]);
                return sorted.length > 0 ? { name: sorted[0][0], value: sorted[0][1] } : { name: 'N/A', value: 0 };
            })(),
        };
    }, [compareEnabled, previousPeriodItems, filterRestaurant, filterItem, totalWasteValue, totalWasteEvents, dailyAverage, filterStartDate, filterEndDate]);

    // Merge current + previous trend data for charts (defined after comparisonMetrics)
    const chartData = useMemo(() => {
        if (compareEnabled && comparisonMetrics && comparisonMetrics.prevTrendData && comparisonMetrics.prevTrendData.length > 0) {
            const prevData = comparisonMetrics.prevTrendData;
            const maxLen = Math.max(trendData.length, prevData.length);
            const merged = [];
            for (let i = 0; i < maxLen; i++) {
                const current = trendData[i];
                const prev = prevData[i];
                merged.push({
                    date: current ? current.date : `Day ${i + 1}`,
                    value: current ? current.value : 0,
                    count: current ? current.count : 0,
                    prevValue: prev ? prev.value : 0,
                    prevDate: prev ? prev.date : null,
                });
            }
            return merged;
        }
        return trendData;
    }, [trendData, compareEnabled, comparisonMetrics]);

    // Delete handlers
    const handleDeleteClick = (item) => {
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!itemToDelete) return;

        setDeleteLoading(true);
        try {
            await deleteDoc(doc(db, 'wastage', itemToDelete.id));
            setWasteItems(prev => prev.filter(item => item.id !== itemToDelete.id));
            setSnackbarMessage('Waste item deleted successfully');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setDeleteDialogOpen(false);
            setItemToDelete(null);
        } catch (error) {
            console.error('Error deleting waste item:', error);
            setSnackbarMessage('Failed to delete waste item');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleRefresh = () => {
        setLoading(true);
        // Trigger data refresh by updating dates
        setFilterEndDate(formatDate(new Date()));
    };

    // Bulk delete handlers
    const handleBulkDeleteClick = () => {
        setBulkDeleteDialogOpen(true);
    };

    const handleBulkDeleteConfirm = async () => {
        setBulkDeleteLoading(true);
        try {
            for (const id of selectedRows) {
                await deleteDoc(doc(db, 'wastage', id));
            }
            setWasteItems(prev => prev.filter(item => !selectedRows.includes(item.id)));
            setSnackbarMessage(`${selectedRows.length} waste items deleted successfully`);
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setSelectedRows([]);
            setBulkDeleteDialogOpen(false);
        } catch (error) {
            console.error('Error deleting waste items:', error);
            setSnackbarMessage('Failed to delete some waste items');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setBulkDeleteLoading(false);
        }
    };

    // DataGrid columns
    const columns = [
        {
            field: 'date',
            headerName: 'Date',
            width: isMobile ? 100 : 120,
            renderCell: (params) => (
                <Tooltip title={params.value}>
                    <span>{params.value}</span>
                </Tooltip>
            )
        },
        {
            field: 'itemName',
            headerName: 'Item',
            width: isMobile ? 130 : 180,
            flex: isMobile ? 0 : 1,
            renderCell: (params) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                    <RestaurantMenu sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" noWrap>{params.value}</Typography>
                </Stack>
            )
        },
        {
            field: 'wastedQuantity',
            headerName: 'Qty',
            width: isMobile ? 90 : 120,
            renderCell: (params) => (
                <Chip
                    label={`${params.row.wastedQuantity} ${params.row.unit || ''}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                />
            ),
        },
        {
            field: 'estimatedValue',
            headerName: 'Value',
            width: isMobile ? 90 : 120,
            renderCell: (params) => (
                <Typography variant="body2" color="error.main" fontWeight="medium">
                    £{parseFloat(params.row.estimatedValue).toFixed(2)}
                </Typography>
            ),
        },
        {
            field: 'reason',
            headerName: 'Reason',
            width: isMobile ? 110 : 160,
            renderCell: (params) => (
                <Tooltip title={params.value}>
                    <Typography variant="body2" noWrap>
                        {params.value}
                    </Typography>
                </Tooltip>
            )
        },
        {
            field: 'restaurant',
            headerName: 'Restaurant',
            width: isMobile ? 110 : 150,
            valueGetter: (params) => {
                if (!params.row?.userId) return 'Unknown';
                const rest = restaurants.find(r => r.id === params.row.userId);
                return rest?.name ?? 'Unknown';
            },
            renderCell: (params) => (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Restaurant sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="body2" noWrap>{params.value}</Typography>
                </Stack>
            )
        },
        {
            field: 'imageUrl',
            headerName: 'Image',
            width: 80,
            sortable: false,
            renderCell: (params) => {
                const url = params.row.imageUrl;
                if (!url || url.startsWith('file://')) {
                    return <ImageIcon sx={{ fontSize: 20, color: 'text.disabled' }} />;
                }
                return (
                    <Tooltip title="Click to view">
                        <IconButton
                            size="small"
                            onClick={() => { setImageDialogUrl(url); setImageDialogOpen(true); }}
                        >
                            <Box
                                component="img"
                                src={url}
                                sx={{ width: 36, height: 36, borderRadius: 1, objectFit: 'cover' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        </IconButton>
                    </Tooltip>
                );
            }
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: isMobile ? 80 : 100,
            sortable: false,
            renderCell: (params) => (
                <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Delete">
                        <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(params.row)}
                        >
                            <Delete fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            ),
        },
    ];

    // KPI Cards
    const renderKPICards = () => (
        <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
                <Card
                    sx={{
                        height: '100%',
                        background: CHART_COLORS.gradients.error,
                        color: 'white',
                        transition: 'transform 0.3s, box-shadow 0.3s',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: theme.shadows[8],
                        }
                    }}
                >
                    <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Box>
                                <Typography variant="overline" sx={{ opacity: 0.8 }}>
                                    Total Waste Value
                                </Typography>
                                <Typography variant="h4" fontWeight="bold">
                                    £{totalWasteValue.toFixed(2)}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center" mt={1}>
                                    {compareEnabled && comparisonMetrics ? (
                                        <>
                                            {comparisonMetrics.valueChange <= 0 ? <TrendingDown sx={{ fontSize: 16 }} /> : <TrendingUp sx={{ fontSize: 16 }} />}
                                            <Typography variant="caption">
                                                {comparisonMetrics.valueChange >= 0 ? '+' : ''}{comparisonMetrics.valueChange.toFixed(1)}% vs prev
                                            </Typography>
                                        </>
                                    ) : (
                                        <>
                                            <TrendingUp sx={{ fontSize: 16 }} />
                                            <Typography variant="caption">
                                                £{periodStats.weekly.value.toFixed(2)} this week
                                            </Typography>
                                        </>
                                    )}
                                </Stack>
                            </Box>
                            <Avatar sx={{ bgcolor: alpha('#fff', 0.2) }}>
                                <AttachMoney />
                            </Avatar>
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <Card
                    sx={{
                        height: '100%',
                        background: CHART_COLORS.gradients.primary,
                        color: 'white',
                        transition: 'transform 0.3s, box-shadow 0.3s',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: theme.shadows[8],
                        }
                    }}
                >
                    <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Box>
                                <Typography variant="overline" sx={{ opacity: 0.8 }}>
                                    Waste Events
                                </Typography>
                                <Typography variant="h4" fontWeight="bold">
                                    {totalWasteEvents}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center" mt={1}>
                                    {compareEnabled && comparisonMetrics ? (
                                        <>
                                            {comparisonMetrics.eventsChange <= 0 ? <TrendingDown sx={{ fontSize: 16 }} /> : <TrendingUp sx={{ fontSize: 16 }} />}
                                            <Typography variant="caption">
                                                {comparisonMetrics.eventsChange >= 0 ? '+' : ''}{comparisonMetrics.eventsChange.toFixed(1)}% vs prev
                                            </Typography>
                                        </>
                                    ) : (
                                        <>
                                            <Assessment sx={{ fontSize: 16 }} />
                                            <Typography variant="caption">
                                                {periodStats.daily.quantity} today
                                            </Typography>
                                        </>
                                    )}
                                </Stack>
                            </Box>
                            <Avatar sx={{ bgcolor: alpha('#fff', 0.2) }}>
                                <Assessment />
                            </Avatar>
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <Card
                    sx={{
                        height: '100%',
                        background: CHART_COLORS.gradients.warning,
                        color: 'white',
                        transition: 'transform 0.3s, box-shadow 0.3s',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: theme.shadows[8],
                        }
                    }}
                >
                    <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Box>
                                <Typography variant="overline" sx={{ opacity: 0.8 }}>
                                    Daily Average
                                </Typography>
                                <Typography variant="h4" fontWeight="bold">
                                    £{dailyAverage.toFixed(2)}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center" mt={1}>
                                    {compareEnabled && comparisonMetrics ? (
                                        <>
                                            {comparisonMetrics.avgChange <= 0 ? <TrendingDown sx={{ fontSize: 16 }} /> : <TrendingUp sx={{ fontSize: 16 }} />}
                                            <Typography variant="caption">
                                                {comparisonMetrics.avgChange >= 0 ? '+' : ''}{comparisonMetrics.avgChange.toFixed(1)}% vs prev
                                            </Typography>
                                        </>
                                    ) : (
                                        <>
                                            <Speed sx={{ fontSize: 16 }} />
                                            <Typography variant="caption">
                                                per day
                                            </Typography>
                                        </>
                                    )}
                                </Stack>
                            </Box>
                            <Avatar sx={{ bgcolor: alpha('#fff', 0.2) }}>
                                <Timeline />
                            </Avatar>
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <Card
                    sx={{
                        height: '100%',
                        background: CHART_COLORS.gradients.info,
                        color: 'white',
                        transition: 'transform 0.3s, box-shadow 0.3s',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: theme.shadows[8],
                        }
                    }}
                >
                    <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Box>
                                <Typography variant="overline" sx={{ opacity: 0.8 }}>
                                    Top Waster
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" noWrap>
                                    {topWastingRestaurant.name}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center" mt={1}>
                                    <Warning sx={{ fontSize: 16 }} />
                                    <Typography variant="caption">
                                        £{topWastingRestaurant.value.toFixed(2)}
                                    </Typography>
                                </Stack>
                            </Box>
                            <Avatar sx={{ bgcolor: alpha('#fff', 0.2) }}>
                                <Restaurant />
                            </Avatar>
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );

    // Mobile Filters Drawer
    const renderMobileFilters = () => (
        <Drawer
            anchor="right"
            open={mobileFiltersOpen}
            onClose={() => setMobileFiltersOpen(false)}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 400 },
                    p: 2
                }
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">Filters</Typography>
                <IconButton onClick={() => setMobileFiltersOpen(false)}>
                    <Close />
                </IconButton>
            </Stack>

            <Stack spacing={3}>
                <FormControl fullWidth size="small">
                    <InputLabel>Time Period</InputLabel>
                    <Select
                        value={timePeriod}
                        label="Time Period"
                        onChange={(e) => setTimePeriod(e.target.value)}
                    >
                        {TIME_PERIODS.map((period) => (
                            <MenuItem key={period.value} value={period.value}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    {period.icon}
                                    <span>{period.label}</span>
                                </Stack>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {timePeriod === 'custom' && (
                    <>
                        <TextField
                            fullWidth
                            size="small"
                            label="Start Date"
                            type="date"
                            value={filterStartDate}
                            onChange={(e) => setFilterStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label="End Date"
                            type="date"
                            value={filterEndDate}
                            onChange={(e) => setFilterEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </>
                )}

                <FormControl fullWidth size="small">
                    <InputLabel>Restaurant</InputLabel>
                    <Select
                        value={filterRestaurant}
                        label="Restaurant"
                        onChange={(e) => setFilterRestaurant(e.target.value)}
                    >
                        <MenuItem value="">
                            <em>All Restaurants</em>
                        </MenuItem>
                        {restaurants.map((r) => (
                            <MenuItem key={r.id} value={r.id}>
                                {r.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Autocomplete
                    options={itemsList}
                    value={filterItem}
                    onChange={(_e, v) => setFilterItem(v)}
                    renderInput={(params) => (
                        <TextField {...params} label="Item" size="small" />
                    )}
                />

                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setMobileFiltersOpen(false)}
                    startIcon={<FilterList />}
                >
                    Apply Filters
                </Button>
            </Stack>
        </Drawer>
    );

    return (
        <Box sx={{
            p: { xs: 2, sm: 3 },
            minHeight: '100vh',
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#f5f5f5',
            width: '100vw',
            maxWidth: '100%',  // Already present
            overflow: 'hidden',  // Already present
            display: 'flex',     // ADD: Enable flex behavior
            flexDirection: 'column', // ADD: Stack content vertically
            flex: 1
        }}>
            {/* Header */}
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                spacing={2}
                sx={{ mb: 3 }}
            >
                <Box>
                    <Typography
                        variant="h1"
                        sx={{
                            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                            fontWeight: 700,
                            color: 'text.primary',
                            mb: 0.5
                        }}
                    >
                        Waste Management
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Monitor and analyze food waste across all restaurants
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1}>
                    {isMobile && (
                        <Tooltip title="Filters">
                            <IconButton
                                color="primary"
                                onClick={() => setMobileFiltersOpen(true)}
                                sx={{
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                    '&:hover': { backgroundColor: 'primary.dark' }
                                }}
                            >
                                <FilterList />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="Refresh Data">
                        <IconButton color="primary" onClick={handleRefresh}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            {/* Desktop Filters */}
            {!isMobile && (
                <Card sx={{ mb: 3, p: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={timePeriod === 'custom' ? 2 : 3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Time Period</InputLabel>
                                <Select
                                    value={timePeriod}
                                    label="Time Period"
                                    onChange={(e) => setTimePeriod(e.target.value)}
                                >
                                    {TIME_PERIODS.map((period) => (
                                        <MenuItem key={period.value} value={period.value}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                {period.icon}
                                                <span>{period.label}</span>
                                            </Stack>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {timePeriod === 'custom' && (
                            <>
                                <Grid item xs={12} md={2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Start Date"
                                        type="date"
                                        value={filterStartDate}
                                        onChange={(e) => setFilterStartDate(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="End Date"
                                        type="date"
                                        value={filterEndDate}
                                        onChange={(e) => setFilterEndDate(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12} md={timePeriod === 'custom' ? 2 : 3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Restaurant</InputLabel>
                                <Select
                                    value={filterRestaurant}
                                    label="Restaurant"
                                    onChange={(e) => setFilterRestaurant(e.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>All Restaurants</em>
                                    </MenuItem>
                                    {restaurants.map((r) => (
                                        <MenuItem key={r.id} value={r.id}>
                                            {r.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={timePeriod === 'custom' ? 4 : 6}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Autocomplete
                                    options={itemsList}
                                    value={filterItem}
                                    onChange={(_e, v) => setFilterItem(v)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Search Items" size="small" />
                                    )}
                                    sx={{ flex: 1 }}
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={compareEnabled}
                                            onChange={(e) => setCompareEnabled(e.target.checked)}
                                            color="primary"
                                            size="small"
                                        />
                                    }
                                    label={
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                            <CompareArrows sx={{ fontSize: 18 }} />
                                            <Typography variant="body2" noWrap>Compare</Typography>
                                        </Stack>
                                    }
                                    sx={{ ml: 0, whiteSpace: 'nowrap' }}
                                />
                            </Stack>
                        </Grid>
                    </Grid>
                </Card>
            )}

            {/* Tabs */}
            <Paper sx={{ mb: 3, borderRadius: 2, overflow: 'hidden', boxShadow: theme.shadows[1] }}>
                <Tabs
                    value={currentTab}
                    onChange={(e, newValue) => setCurrentTab(newValue)}
                    variant={isMobile ? "scrollable" : "standard"}
                    scrollButtons={isMobile ? "auto" : false}
                    sx={{
                        '& .MuiTab-root': {
                            minHeight: 60,
                            fontWeight: 600,
                        }
                    }}
                >
                    <Tab
                        label={isMobile ? "Overview" : "Dashboard Overview"}
                        icon={<Dashboard />}
                        iconPosition="start"
                    />
                    <Tab
                        label={isMobile ? "Insights" : "Analytics Insights"}
                        icon={<Insights />}
                        iconPosition="start"
                    />
                    <Tab
                        label={isMobile ? "Compare" : "Restaurant Compare"}
                        icon={<CompareArrows />}
                        iconPosition="start"
                    />
                    <Tab
                        label={isMobile ? "Details" : "Detailed View"}
                        icon={<ListIcon />}
                        iconPosition="start"
                    />
                </Tabs>
            </Paper>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {/* Tab 0: Overview */}
                    {currentTab === 0 && (
                        <Fade in={true}>
                            <Box>
                                {renderKPICards()}

                                {/* Comparison Summary Card */}
                                {compareEnabled && comparisonMetrics && (
                                    <Card sx={{ mb: 3, border: `2px solid ${theme.palette.primary.main}`, borderRadius: 2, overflow: 'hidden' }}>
                                        <Box sx={{ background: theme.palette.primary.main, px: 2.5, py: 1.5 }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <CompareArrows sx={{ color: '#fff', fontSize: 22 }} />
                                                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#fff' }}>
                                                        Period Comparison
                                                    </Typography>
                                                </Stack>
                                                {previousPeriodLoading && <CircularProgress size={20} sx={{ color: '#fff' }} />}
                                            </Stack>
                                        </Box>
                                        <CardContent sx={{ p: 0 }}>
                                            {/* Date Range Labels */}
                                            <Grid container>
                                                <Grid item xs={5} sx={{ p: 2, borderRight: `1px solid ${theme.palette.divider}`, textAlign: 'center', bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                                                    <Typography variant="overline" color="text.secondary" fontWeight={600}>Current Period</Typography>
                                                    <Typography variant="body2" fontWeight={700} color="primary.main">
                                                        {filterStartDate} → {filterEndDate}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <Typography variant="h6" color="text.secondary" fontWeight={700}>vs</Typography>
                                                </Grid>
                                                <Grid item xs={5} sx={{ p: 2, borderLeft: `1px solid ${theme.palette.divider}`, textAlign: 'center', bgcolor: alpha('#ff9800', 0.04) }}>
                                                    <Typography variant="overline" color="text.secondary" fontWeight={600}>Previous Period</Typography>
                                                    <Typography variant="body2" fontWeight={700} sx={{ color: '#ff9800' }}>
                                                        {comparisonMetrics.prevStartDate} → {comparisonMetrics.prevEndDate}
                                                    </Typography>
                                                </Grid>
                                            </Grid>

                                            <Divider />

                                            {/* Metrics Comparison Grid */}
                                            <Grid container sx={{ textAlign: 'center' }}>
                                                {/* Waste Value */}
                                                <Grid item xs={12} sm={6} md={3} sx={{ p: 2, borderRight: { md: `1px solid ${theme.palette.divider}` }, borderBottom: { xs: `1px solid ${theme.palette.divider}`, md: 'none' } }}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>TOTAL VALUE</Typography>
                                                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} mt={1}>
                                                        <Box>
                                                            <Typography variant="body2" color="text.secondary">Current</Typography>
                                                            <Typography variant="h6" fontWeight={700} color="primary.main">£{totalWasteValue.toFixed(2)}</Typography>
                                                        </Box>
                                                        <Typography variant="body2" color="text.secondary">vs</Typography>
                                                        <Box>
                                                            <Typography variant="body2" color="text.secondary">Previous</Typography>
                                                            <Typography variant="h6" fontWeight={700} sx={{ color: '#ff9800' }}>£{comparisonMetrics.prevTotalValue.toFixed(2)}</Typography>
                                                        </Box>
                                                    </Stack>
                                                    <Chip
                                                        size="small"
                                                        icon={comparisonMetrics.valueChange <= 0 ? <TrendingDown sx={{ fontSize: 16 }} /> : <TrendingUp sx={{ fontSize: 16 }} />}
                                                        label={`${comparisonMetrics.valueChange >= 0 ? '+' : ''}${comparisonMetrics.valueChange.toFixed(1)}%`}
                                                        sx={{ mt: 1, fontWeight: 700, bgcolor: comparisonMetrics.valueChange <= 0 ? alpha('#4caf50', 0.1) : alpha('#f44336', 0.1), color: comparisonMetrics.valueChange <= 0 ? '#4caf50' : '#f44336' }}
                                                    />
                                                </Grid>

                                                {/* Waste Events */}
                                                <Grid item xs={12} sm={6} md={3} sx={{ p: 2, borderRight: { md: `1px solid ${theme.palette.divider}` }, borderBottom: { xs: `1px solid ${theme.palette.divider}`, md: 'none' } }}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>WASTE EVENTS</Typography>
                                                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} mt={1}>
                                                        <Box>
                                                            <Typography variant="body2" color="text.secondary">Current</Typography>
                                                            <Typography variant="h6" fontWeight={700} color="primary.main">{totalWasteEvents}</Typography>
                                                        </Box>
                                                        <Typography variant="body2" color="text.secondary">vs</Typography>
                                                        <Box>
                                                            <Typography variant="body2" color="text.secondary">Previous</Typography>
                                                            <Typography variant="h6" fontWeight={700} sx={{ color: '#ff9800' }}>{comparisonMetrics.prevTotalEvents}</Typography>
                                                        </Box>
                                                    </Stack>
                                                    <Chip
                                                        size="small"
                                                        icon={comparisonMetrics.eventsChange <= 0 ? <TrendingDown sx={{ fontSize: 16 }} /> : <TrendingUp sx={{ fontSize: 16 }} />}
                                                        label={`${comparisonMetrics.eventsChange >= 0 ? '+' : ''}${comparisonMetrics.eventsChange.toFixed(1)}%`}
                                                        sx={{ mt: 1, fontWeight: 700, bgcolor: comparisonMetrics.eventsChange <= 0 ? alpha('#4caf50', 0.1) : alpha('#f44336', 0.1), color: comparisonMetrics.eventsChange <= 0 ? '#4caf50' : '#f44336' }}
                                                    />
                                                </Grid>

                                                {/* Daily Average */}
                                                <Grid item xs={12} sm={6} md={3} sx={{ p: 2, borderRight: { md: `1px solid ${theme.palette.divider}` }, borderBottom: { xs: `1px solid ${theme.palette.divider}`, md: 'none' } }}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>DAILY AVERAGE</Typography>
                                                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} mt={1}>
                                                        <Box>
                                                            <Typography variant="body2" color="text.secondary">Current</Typography>
                                                            <Typography variant="h6" fontWeight={700} color="primary.main">£{dailyAverage.toFixed(2)}</Typography>
                                                        </Box>
                                                        <Typography variant="body2" color="text.secondary">vs</Typography>
                                                        <Box>
                                                            <Typography variant="body2" color="text.secondary">Previous</Typography>
                                                            <Typography variant="h6" fontWeight={700} sx={{ color: '#ff9800' }}>£{comparisonMetrics.prevDailyAvg.toFixed(2)}</Typography>
                                                        </Box>
                                                    </Stack>
                                                    <Chip
                                                        size="small"
                                                        icon={comparisonMetrics.avgChange <= 0 ? <TrendingDown sx={{ fontSize: 16 }} /> : <TrendingUp sx={{ fontSize: 16 }} />}
                                                        label={`${comparisonMetrics.avgChange >= 0 ? '+' : ''}${comparisonMetrics.avgChange.toFixed(1)}%`}
                                                        sx={{ mt: 1, fontWeight: 700, bgcolor: comparisonMetrics.avgChange <= 0 ? alpha('#4caf50', 0.1) : alpha('#f44336', 0.1), color: comparisonMetrics.avgChange <= 0 ? '#4caf50' : '#f44336' }}
                                                    />
                                                </Grid>

                                                {/* Top Waste Item */}
                                                <Grid item xs={12} sm={6} md={3} sx={{ p: 2 }}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>TOP WASTE ITEM</Typography>
                                                    <Box mt={1}>
                                                        <Typography variant="body2" color="text.secondary">Current</Typography>
                                                        <Typography variant="body1" fontWeight={700} color="primary.main" noWrap>{comparisonMetrics.currentTopItem.name}</Typography>
                                                        <Typography variant="caption" color="text.secondary">£{comparisonMetrics.currentTopItem.value.toFixed(2)}</Typography>
                                                    </Box>
                                                    <Box mt={0.5}>
                                                        <Typography variant="body2" color="text.secondary">Previous</Typography>
                                                        <Typography variant="body1" fontWeight={700} sx={{ color: '#ff9800' }} noWrap>{comparisonMetrics.prevTopItem.name}</Typography>
                                                        <Typography variant="caption" color="text.secondary">£{comparisonMetrics.prevTopItem.value.toFixed(2)}</Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                )}

                                <Grid container spacing={3}>
                                    <Grid item xs={12} lg={8}>
                                        <Card sx={{
                                            height: '100%',
                                            transition: 'transform 0.3s',
                                            '&:hover': { transform: 'translateY(-2px)' }
                                        }}>
                                            <CardContent>
                                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                                                    <Typography variant="h6" fontWeight={600}>
                                                        Waste Trend Over Time
                                                    </Typography>
                                                    <ButtonGroup size="small">
                                                        <Button
                                                            variant={chartType === CHART_TYPES.AREA ? 'contained' : 'outlined'}
                                                            onClick={() => setChartType(CHART_TYPES.AREA)}
                                                            startIcon={<ShowChart />}
                                                        >
                                                            {!isMobile && 'Area'}
                                                        </Button>
                                                        <Button
                                                            variant={chartType === CHART_TYPES.LINE ? 'contained' : 'outlined'}
                                                            onClick={() => setChartType(CHART_TYPES.LINE)}
                                                            startIcon={<Timeline />}
                                                        >
                                                            {!isMobile && 'Line'}
                                                        </Button>
                                                        <Button
                                                            variant={chartType === CHART_TYPES.BAR ? 'contained' : 'outlined'}
                                                            onClick={() => setChartType(CHART_TYPES.BAR)}
                                                            startIcon={<BarChartIcon />}
                                                        >
                                                            {!isMobile && 'Bar'}
                                                        </Button>
                                                    </ButtonGroup>
                                                </Stack>
                                                <ResponsiveContainer width="100%" height={300}>
                                                    {chartType === CHART_TYPES.AREA ? (
                                                        <AreaChart data={chartData}>
                                                            <defs>
                                                                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                                                                    <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.8} />
                                                                    <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0} />
                                                                </linearGradient>
                                                            </defs>
                                                            <CartesianGrid strokeDasharray="3 3" />
                                                            <XAxis dataKey="date" />
                                                            <YAxis />
                                                            <ReTooltip />
                                                            {compareEnabled && comparisonMetrics && <Legend />}
                                                            <Area type="monotone" dataKey="value" name="Current Period" stroke={CHART_COLORS.primary} fillOpacity={1} fill="url(#colorValue)" />
                                                            {compareEnabled && comparisonMetrics && comparisonMetrics.prevTrendData.length > 0 && (
                                                                <Area type="monotone" dataKey="prevValue" name="Previous Period" stroke="#ff9800" fillOpacity={0.3} fill="#ff9800" strokeDasharray="5 5" />
                                                            )}
                                                        </AreaChart>
                                                    ) : chartType === CHART_TYPES.LINE ? (
                                                        <LineChart data={chartData}>
                                                            <CartesianGrid strokeDasharray="3 3" />
                                                            <XAxis dataKey="date" />
                                                            <YAxis />
                                                            <ReTooltip />
                                                            <Legend />
                                                            <Line type="monotone" dataKey="value" name="Current Period" stroke={CHART_COLORS.primary} strokeWidth={2} />
                                                            {compareEnabled && comparisonMetrics && comparisonMetrics.prevTrendData.length > 0 && (
                                                                <Line type="monotone" dataKey="prevValue" name="Previous Period" stroke="#ff9800" strokeWidth={2} strokeDasharray="5 5" />
                                                            )}
                                                        </LineChart>
                                                    ) : (
                                                        <BarChart data={chartData}>
                                                            <CartesianGrid strokeDasharray="3 3" />
                                                            <XAxis dataKey="date" />
                                                            <YAxis />
                                                            <ReTooltip />
                                                            {compareEnabled && comparisonMetrics && <Legend />}
                                                            <Bar dataKey="value" name="Current Period" fill={CHART_COLORS.primary} />
                                                            {compareEnabled && comparisonMetrics && comparisonMetrics.prevTrendData.length > 0 && (
                                                                <Bar dataKey="prevValue" name="Previous Period" fill="#ff9800" opacity={0.6} />
                                                            )}
                                                        </BarChart>
                                                    )}
                                                </ResponsiveContainer>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} lg={4}>
                                        <Card sx={{ height: '100%' }}>
                                            <CardContent>
                                                <Typography variant="h6" fontWeight={600} mb={3}>
                                                    Waste by Reason
                                                </Typography>
                                                <ResponsiveContainer width="100%" height={300}>
                                                    <PieChart>
                                                        <Pie
                                                            data={reasonPieData}
                                                            dataKey="value"
                                                            nameKey="name"
                                                            cx="50%"
                                                            cy="45%"
                                                            innerRadius={40}
                                                            outerRadius={75}
                                                            paddingAngle={2}
                                                            label={({ name, percent }) => percent > 0.08 ? `${(percent * 100).toFixed(0)}%` : ''}
                                                            labelLine={false}
                                                        >
                                                            {reasonPieData.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={CHART_COLORS.pastel[index % CHART_COLORS.pastel.length]} />
                                                            ))}
                                                        </Pie>
                                                        <ReTooltip formatter={(value) => `£${value.toFixed(2)}`} />
                                                        <Legend
                                                            layout="horizontal"
                                                            verticalAlign="bottom"
                                                            align="center"
                                                            wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                                                            formatter={(value, entry) => `${value}: £${entry.payload.value.toFixed(0)}`}
                                                        />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>

                                {/* Quick Stats */}
                                <Grid container spacing={2} sx={{ mt: 2 }}>
                                    <Grid item xs={6} sm={3}>
                                        <Card sx={{ p: 2, textAlign: 'center' }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Daily Avg
                                            </Typography>
                                            <Typography variant="h6" color="primary.main">
                                                £{dailyAverage.toFixed(2)}
                                            </Typography>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Card sx={{ p: 2, textAlign: 'center' }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Weekly Total
                                            </Typography>
                                            <Typography variant="h6" color="secondary.main">
                                                £{periodStats.weekly.value.toFixed(2)}
                                            </Typography>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Card sx={{ p: 2, textAlign: 'center' }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Top Item
                                            </Typography>
                                            <Typography variant="h6" color="warning.main" noWrap>
                                                {topWastedItem.name}
                                            </Typography>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Card sx={{ p: 2, textAlign: 'center' }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Main Reason
                                            </Typography>
                                            <Typography variant="h6" color="error.main" noWrap>
                                                {mainWasteReason}
                                            </Typography>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Fade>
                    )}

                    {/* Tab 1: Analytics Insights */}
                    {currentTab === 1 && (
                        <Fade in={true}>
                            <Box>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h6" mb={3}>Top Wasted Items</Typography>
                                                <ResponsiveContainer width="100%" height={400}>
                                                    <BarChart
                                                        data={Object.entries(
                                                            filteredWasteItems.reduce((acc, item) => {
                                                                const name = item.itemName || 'Unknown';
                                                                const value = parseFloat(item.estimatedValue) || 0;
                                                                acc[name] = (acc[name] || 0) + value;
                                                                return acc;
                                                            }, {})
                                                        ).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 10)}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                                                        <YAxis />
                                                        <ReTooltip />
                                                        <Bar dataKey="value" fill={CHART_COLORS.secondary} />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h6" mb={3}>Waste Distribution by Reason</Typography>
                                                <ResponsiveContainer width="100%" height={300}>
                                                    <BarChart data={reasonPieData}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="name" />
                                                        <YAxis />
                                                        <ReTooltip />
                                                        <Bar dataKey="value" fill={CHART_COLORS.info} />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h6" mb={3}>Monthly Trend</Typography>
                                                <ResponsiveContainer width="100%" height={300}>
                                                    <LineChart data={trendData}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="date" />
                                                        <YAxis />
                                                        <ReTooltip />
                                                        <Line type="monotone" dataKey="count" stroke={CHART_COLORS.success} name="Count" />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Fade>
                    )}

                    {/* Tab 2: Restaurant Comparison */}
                    {currentTab === 2 && (
                        <Fade in={true}>
                            <Box>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h6" mb={3}>Restaurant Waste Comparison</Typography>
                                                <ResponsiveContainer width="100%" height={400}>
                                                    <BarChart data={restaurantComparisonData}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                                                        <YAxis />
                                                        <ReTooltip />
                                                        <Legend />
                                                        <Bar dataKey="value" fill={CHART_COLORS.primary} name="Total Value (£)" />
                                                        <Bar dataKey="count" fill={CHART_COLORS.secondary} name="Waste Events" />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h6" mb={3}>Restaurant Performance Summary</Typography>
                                                <Grid container spacing={2}>
                                                    {restaurantComparisonData.map((rest, index) => (
                                                        <Grid item xs={12} sm={6} md={4} key={index}>
                                                            <Card variant="outlined">
                                                                <CardContent>
                                                                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                                                                        <Typography variant="subtitle1" fontWeight={600}>
                                                                            {rest.name}
                                                                        </Typography>
                                                                        <Chip
                                                                            label={`#${index + 1}`}
                                                                            size="small"
                                                                            color={index === 0 ? 'error' : index === 1 ? 'warning' : 'default'}
                                                                        />
                                                                    </Stack>
                                                                    <Stack spacing={1}>
                                                                        <Stack direction="row" justifyContent="space-between">
                                                                            <Typography variant="body2" color="text.secondary">
                                                                                Total Waste:
                                                                            </Typography>
                                                                            <Typography variant="body2" fontWeight={600} color="error.main">
                                                                                £{rest.value.toFixed(2)}
                                                                            </Typography>
                                                                        </Stack>
                                                                        <Stack direction="row" justifyContent="space-between">
                                                                            <Typography variant="body2" color="text.secondary">
                                                                                Events:
                                                                            </Typography>
                                                                            <Typography variant="body2" fontWeight={600}>
                                                                                {rest.count}
                                                                            </Typography>
                                                                        </Stack>
                                                                        <Stack direction="row" justifyContent="space-between">
                                                                            <Typography variant="body2" color="text.secondary">
                                                                                Avg per Event:
                                                                            </Typography>
                                                                            <Typography variant="body2" fontWeight={600}>
                                                                                £{(rest.value / rest.count).toFixed(2)}
                                                                            </Typography>
                                                                        </Stack>
                                                                    </Stack>
                                                                </CardContent>
                                                            </Card>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Fade>
                    )}

                    {/* Tab 3: Detailed View */}
                    {currentTab === 3 && (
                        <Box sx={{ mt: 2 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Typography variant="h6">Waste Records</Typography>
                                    <Badge badgeContent={filteredWasteItems.length} color="primary">
                                        <Typography variant="caption">Records</Typography>
                                    </Badge>
                                </Stack>
                                {selectedRows.length > 0 && (
                                    <Button
                                        variant="contained"
                                        color="error"
                                        startIcon={<DeleteSweep />}
                                        onClick={handleBulkDeleteClick}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        Delete Selected ({selectedRows.length})
                                    </Button>
                                )}
                            </Stack>

                            {filteredWasteItems.length === 0 ? (
                                <Paper sx={{ p: 4, textAlign: 'center' }}>
                                    <Typography>No records found</Typography>
                                </Paper>
                            ) : (
                                <Paper sx={{ height: 500, width: '100%' }}>
                                    <DataGrid
                                        rows={filteredWasteItems}
                                        columns={[
                                            { field: 'date', headerName: 'Date', width: 120 },
                                            { field: 'itemName', headerName: 'Item', width: 180 },
                                            {
                                                field: 'wastedQuantity',
                                                headerName: 'Qty',
                                                width: 100,
                                                renderCell: (params) => (
                                                    <Chip
                                                        label={`${params.row.wastedQuantity || 0} ${params.row.unit || ''}`}
                                                        size="small"
                                                        color="primary"
                                                    />
                                                )
                                            },
                                            {
                                                field: 'estimatedValue',
                                                headerName: 'Value',
                                                width: 120,
                                                renderCell: (params) => (
                                                    <Typography>
                                                        £{(parseFloat(params.row.estimatedValue || 0).toFixed(2))}
                                                    </Typography>
                                                )
                                            },
                                            { field: 'reason', headerName: 'Reason', width: 150 },
                                            {
                                                field: 'userId',
                                                headerName: 'Restaurant',
                                                width: 150,
                                                renderCell: (params) => {
                                                    const rest = restaurants.find(r => r.id === params.row.userId);
                                                    return rest?.name || 'Unknown';
                                                }
                                            },
                                            {
                                                field: 'imageUrl',
                                                headerName: 'Image',
                                                width: 80,
                                                sortable: false,
                                                renderCell: (params) => {
                                                    const url = params.row.imageUrl;
                                                    if (!url || url.startsWith('file://')) {
                                                        return <ImageIcon sx={{ fontSize: 20, color: 'text.disabled' }} />;
                                                    }
                                                    return (
                                                        <Tooltip title="Click to view">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => { setImageDialogUrl(url); setImageDialogOpen(true); }}
                                                            >
                                                                <Box
                                                                    component="img"
                                                                    src={url}
                                                                    sx={{ width: 36, height: 36, borderRadius: 1, objectFit: 'cover' }}
                                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                                />
                                                            </IconButton>
                                                        </Tooltip>
                                                    );
                                                }
                                            },
                                            {
                                                field: 'actions',
                                                headerName: 'Actions',
                                                width: 100,
                                                sortable: false,
                                                renderCell: (params) => (
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleDeleteClick(params.row)}
                                                        >
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )
                                            }
                                        ]}
                                        getRowId={(row) => row.id}
                                        pageSizeOptions={[5, 10, 25]}
                                        checkboxSelection
                                        onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
                                        rowSelectionModel={selectedRows}
                                        sx={{
                                            border: 'none',
                                            '& .MuiDataGrid-columnHeaders': {
                                                backgroundColor: 'background.paper'
                                            }
                                        }}
                                    />
                                </Paper>
                            )}
                        </Box>
                    )}

                </>
            )}

            {/* Mobile Filters Drawer */}
            {renderMobileFilters()}

            {/* Delete Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Warning color="error" />
                        <Typography variant="h6">Confirm Deletion</Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this waste entry?
                        <Box sx={{ mt: 2, p: 2, backgroundColor: alpha(theme.palette.error.main, 0.1), borderRadius: 1 }}>
                            <Typography variant="subtitle2" color="error">
                                <strong>Item:</strong> {itemToDelete?.itemName}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Quantity:</strong> {itemToDelete?.wastedQuantity} {itemToDelete?.unit}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Date:</strong> {itemToDelete?.date}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Value:</strong> £{itemToDelete?.estimatedValue}
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            This action cannot be undone.
                        </Typography>
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}
                        disabled={deleteLoading}
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        disabled={deleteLoading}
                        startIcon={deleteLoading ? <CircularProgress size={16} /> : <Delete />}
                    >
                        {deleteLoading ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Delete Dialog */}
            <Dialog
                open={bulkDeleteDialogOpen}
                onClose={() => setBulkDeleteDialogOpen(false)}
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <DeleteSweep color="error" />
                        <Typography variant="h6">Bulk Delete</Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete <strong>{selectedRows.length}</strong> waste entries?
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            This action cannot be undone.
                        </Typography>
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button
                        onClick={() => setBulkDeleteDialogOpen(false)}
                        disabled={bulkDeleteLoading}
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleBulkDeleteConfirm}
                        color="error"
                        variant="contained"
                        disabled={bulkDeleteLoading}
                        startIcon={bulkDeleteLoading ? <CircularProgress size={16} /> : <DeleteSweep />}
                    >
                        {bulkDeleteLoading ? 'Deleting...' : `Delete ${selectedRows.length} Items`}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Image Preview Dialog */}
            <Dialog
                open={imageDialogOpen}
                onClose={() => setImageDialogOpen(false)}
                maxWidth="md"
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Waste Image</Typography>
                        <IconButton onClick={() => setImageDialogOpen(false)}>
                            <Close />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Box
                        component="img"
                        src={imageDialogUrl}
                        sx={{
                            width: '100%',
                            maxHeight: '70vh',
                            objectFit: 'contain',
                            borderRadius: 1,
                        }}
                        onError={(e) => {
                            e.target.src = '';
                            e.target.alt = 'Failed to load image';
                        }}
                    />
                </DialogContent>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                TransitionComponent={Fade}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    variant="filled"
                    sx={{
                        width: '100%',
                        borderRadius: 2,
                        boxShadow: theme.shadows[3]
                    }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default EnhancedWasteManagementTab;